/**
 * v1 API client — SPA architectural convergence plan §6.1.
 *
 * Replaces the Phase-0 polling patterns in the legacy
 * `rmf-client.ts`. The legacy client called the upstream Zenoh REST
 * plugin directly with no auth and aggregated `Array<{key, value,
 * encoding, timestamp}>` client-side; the v1 client uses the
 * global `nexsoss-api-server` REST surface with ZITADEL Bearer
 * authentication and never reads raw Zenoh payloads.
 *
 * Live reads bypass this client entirely; the browser talks
 * fetch-SSE directly to the internal Zenoh REST/SSE plugin behind
 * Envoy (plan §Locked architecture §5: "The API must not aggregate
 * live current state for browser reads.").
 */
import { getCurrentToken } from '../auth/token-store';
import { assertWriteAllowed } from '../auth/role-preview';
import { getRuntimeConfig } from '../config/runtime-config';
import type {
  AcquireLiftSessionRequest,
  AcquireLiftSessionResponse,
  ApiError,
  AuditEntry,
  BuildingMapSnapshot,
  CommandRecord,
  CreateSupportTicketRequest,
  CreateTaskFavoriteRequest,
  CreateMaintenanceWindowRequest,
  CreateTaskScheduleRequest,
  DispatchTaskRequest,
  DispatchTaskResponse,
  DoorCommandRequest,
  DoorCommandResponse,
  DoorOverrideRequest,
  DoorOverrideResponse,
  DoorRegistry,
  ExecuteTaskFavoriteRequest,
  FireEvent,
  FireModeState,
  FireResolveRequest,
  FireResolveResponse,
  FireTriggerRequest,
  LiftArmRequest,
  LiftArmResponse,
  LiftAbortRequest,
  LiftAbortResponse,
  LiftCommandRequest,
  MaintenanceWindow,
  LiftCommandResponse,
  LiftRegistry,
  LiftSession,
  LiftWriteGate,
  PaginatedAudit,
  PaginatedTaskCompletions,
  PaginatedTaskLog,
  PlaceRegistry,
  PlanCheck,
  ReadyzResponse,
  ReleaseLiftSessionRequest,
  RobotCommissionRequest,
  RobotCommissionResponse,
  RobotConfirmDustbagRequest,
  RobotConfirmDustbagResponse,
  RobotFireRelocalizeRequest,
  RobotFireRelocalizeResponse,
  RobotIdleBehaviorRequest,
  RobotIdleBehaviorResponse,
  RobotIdlePolicy,
  RobotId,
  ScheduleFireStatus,
  ScheduleOccurrence,
  ScheduleOccurrenceOverride,
  ScheduleUpdatePreview,
  SiteCapabilities,
  SiteId,
  SiteSummary,
  SupportTicket,
  TaskCatalogEntry,
  TaskControlRequest,
  TaskControlResponse,
  TaskDistanceList,
  TaskDistanceRecord,
  TaskFavorite,
  TaskId,
  TaskLogEntry,
  TaskRequestRecord,
  TaskSchedule,
  UpdateTaskScheduleRequest,
  UpdateUserNotificationPrefs,
  UserNotificationPrefs,
  LiftAdvanceRequest,
  LiftRecoverRequest,
  LiftEndSessionRequest,
  LiftManualCallRequest,
  LiftManualCloseRequest,
  LiftVerbResponse,
  SupportTicketComment,
  SupportTicketDetail,
  CreateLiftRuleWindowRequest,
  LiftRuleHoldRequest,
  LiftRuleLiftView,
  LiftRuleOccurrencePage,
  LiftRuleScope,
  LiftRulesView,
  LiftRuleWindow,
} from './v1';
import type {
  RobotLastPosesResponse,
  RobotLastPosesResult,
  RobotTrail,
  ZoneFreshnessResponse,
} from './twin';

// ----------------------------------------------------------------------------
// Low-level fetch helpers
// ----------------------------------------------------------------------------

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: Record<string, unknown>;
  /** The response body as parsed, untouched. The lift verdict parser
   *  (`useOperatorRequest.ts::verdictFromError`) reads the 409
   *  pre-flight's `phase`/`wait`/`reason` from here, wherever the
   *  api-server put them. */
  readonly body: unknown;

  constructor(status: number, body: ApiError | { message: string }) {
    // FastAPI serialises `HTTPException(detail={code, message})` as
    // `{"detail": {code, message}}`; the api-server raises that shape
    // everywhere (`api/lifts.py`). Read the nested envelope when the
    // top level carries none, so a 409's code is the api-server's code
    // and not 'internal'.
    const nested =
      'detail' in body && body.detail && typeof body.detail === 'object'
        ? (body.detail as Record<string, unknown>)
        : undefined;
    const message =
      'message' in body && typeof body.message === 'string'
        ? body.message
        : typeof nested?.message === 'string'
          ? nested.message
          : 'api error';
    super(message);
    this.status = status;
    this.code =
      'code' in body && typeof body.code === 'string'
        ? body.code
        : typeof nested?.code === 'string'
          ? nested.code
          : 'internal';
    this.detail = nested;
    this.body = body;
  }
}

/**
 * The one sentence for a request that never reached the server.
 *
 * WHY THIS EXISTS
 * ---------------
 * 2026-08-31, sg-nomad-01 testing day: an operator pressed the
 * automatic-charging toggle and the drawer said **"Failed to fetch"**.
 * That is the browser's own words for `fetch()` rejecting, and the SPA
 * rendered it through the same path as an api-server refusal, so the
 * message read as "the platform said no" when in fact nothing had left
 * the browser at all. The real cause was a CORS preflight refusal — the
 * endpoint is a `PUT` and the api-server's `allow_methods` listed only
 * `GET/POST/DELETE` (fixed in api-server 0.6.11) — and browsers report
 * a refused preflight as exactly this indistinguishable `TypeError`.
 * Diagnosis went looking in the api-server's logs for a request that
 * was never in them.
 *
 * So the copy names the only two things it can honestly name. It does
 * NOT say the server rejected anything, because no HTTP status was ever
 * received; a status here would be an invention.
 */
export const NETWORK_BLOCKED_MESSAGE =
  'Request blocked before reaching the server (network or CORS)';

/**
 * `fetch()` itself rejected: DNS, TLS, a dropped connection, an
 * ad-blocker, or — the case that cost us the testing day — a CORS
 * preflight the browser refused. The web platform deliberately
 * collapses all of these into one opaque `TypeError` with no status and
 * no body, so this class carries what the SPA *does* know (which
 * request, and the browser's original wording) rather than pretending
 * to know more.
 *
 * EXTENDS `TypeError` ON PURPOSE. `retry.ts::shouldRetry` classifies a
 * fetch rejection by `err instanceof TypeError` and that is the correct
 * call — these are transient far more often than not. Wrapping the
 * rejection in a plain `Error` would have silently switched every
 * mutation's retry off, which is the kind of change that only shows up
 * in production. Subclassing keeps that predicate true.
 */
export class ApiNetworkError extends TypeError {
  /** Sits where `ApiClientError.code` does so a caller can log one
   *  field for both. Never an HTTP status — there wasn't one. */
  readonly code = 'network_blocked';
  /** The request that never left, for the console and for logs. */
  readonly method: string;
  readonly url: string;
  /** The browser's own wording ('Failed to fetch' in Chromium,
   *  'Load failed' in Safari, 'NetworkError when attempting to fetch
   *  resource.' in Firefox). Kept because it is the string an engineer
   *  will search for, and dropped from the operator-facing message
   *  because it told the operator nothing. */
  readonly underlyingMessage: string;

  constructor(method: string, url: string, cause: unknown) {
    super(NETWORK_BLOCKED_MESSAGE, { cause });
    this.name = 'ApiNetworkError';
    this.method = method;
    this.url = url;
    this.underlyingMessage = cause instanceof Error ? cause.message : String(cause);
  }
}

/**
 * The messages browsers and Node use for a `fetch()` that never got a
 * response. Matched only as a FALLBACK, for the call sites that talk
 * to `fetch` directly instead of through `apiFetch`. The whole list,
 * re-derived 2026-09-01 by grepping `src/` for a bare `fetch(`:
 * `api/hooks/index.ts`, `api/hooks/useIssues`, `api/hooks/useMetrics`,
 * `api/hooks/useGateways`, `api/hooks/useNotificationPrefs`,
 * `api/v1-zenoh-client`, and — outside `src/api`, so easy to miss when
 * this fallback is finally retired — `screens/Profile/ProfileScreen`
 * (the alerts CSV export) and `config/runtime-config-reload`.
 * `api/fetch-sse` calls `fetch` too and is deliberately NOT on the
 * list: the SSE transport owns its own reconnect/backoff state machine
 * and its own error copy. Anything routed through `apiFetch` arrives
 * as a real `ApiNetworkError` and never needs the string.
 *
 * Deliberately an exact-phrase list rather than "any `TypeError`": a
 * `TypeError` is also what a genuine SPA bug throws ("x is not a
 * function"), and telling an operator that a programming fault was
 * "blocked by the network" would trade one dishonest message for
 * another. No engine produces these phrasings for anything else.
 */
const FETCH_REJECTION_MESSAGES = [
  'failed to fetch', // Chromium
  'load failed', // Safari
  'networkerror when attempting to fetch resource.', // Firefox
  'fetch failed', // Node / undici (jsdom tests, SSR)
  'network request failed', // React Native / older fetch polyfills
];

/**
 * Operator text for a request that never reached the server, or `null`
 * when this error is not that.
 *
 * THE CLASSIFICATION LIVES HERE, once, so that every screen benefits
 * rather than whichever one an incident happened to be about. Callers
 * that render `error.message` directly are already correct without
 * calling this — `ApiNetworkError`'s message IS this sentence — and
 * callers that decorate an error with HTTP details use it to find out
 * that there are no HTTP details to add.
 *
 * `ApiClientError` short-circuits to `null` first and unconditionally:
 * the server answered, and its own message must survive this function
 * untouched.
 */
export function networkFailureText(err: unknown): string | null {
  if (err instanceof ApiClientError) return null;
  if (err instanceof ApiNetworkError) return err.message;
  if (err instanceof TypeError && FETCH_REJECTION_MESSAGES.includes(err.message.toLowerCase())) {
    return NETWORK_BLOCKED_MESSAGE;
  }
  return null;
}

function authHeaders(): HeadersInit {
  const token = getCurrentToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function apiFetch(
  path: string,
  init: RequestInit & { idempotencyKey?: string } = {},
): Promise<Response> {
  const cfg = getRuntimeConfig();
  const url = `${cfg.apiBaseUrl}${path}`;
  // "View as role" is look-only: every write is refused HERE, before a
  // header is built or a byte leaves, with a `PreviewModeError` whose
  // message is the calm "Actions are off while viewing as …". The
  // global fetch guard (`installPreviewFetchGuard`) repeats this for
  // the call sites that bypass `apiFetch`; this one is the v1 client's
  // own and does not depend on that guard being installed.
  assertWriteAllowed(init.method, url);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string> | undefined) ?? {}),
    ...(authHeaders() as Record<string, string>),
  };
  if (init.idempotencyKey) {
    headers['X-Idempotency-Key'] = init.idempotencyKey;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers,
      // `no-store` REMAINS THE DEFAULT for every endpoint: this API serves
      // live operational state, and a browser reusing a cached robot
      // position is worse than a slow one.
      //
      // But it was applied UNCONDITIONALLY, which also defeated caching on
      // the one response where it matters enormously: the building map is
      // ~340KB of base64 PNG per level, and `no-store` means the browser
      // never sends If-None-Match, so the server's ETag/304 support could
      // never engage. Callers that opt in get ordinary HTTP revalidation.
      cache: init.cache ?? 'no-store',
    });
  } catch (err) {
    // `fetch` rejects, so no request/response pair ever existed. The
    // ONE classification point for that in the whole REST surface —
    // every v1 call goes through here — which is why the auto-charge
    // toggle needs no special case of its own.
    //
    // Aborts and timeouts are NOT this and are rethrown untouched. An
    // abort is the SPA's own doing (react-query cancelling a query, a
    // route change, `retry.ts`'s controller) and callers detect it by
    // `name === 'AbortError'`; a timeout means the request may well
    // have reached the server and been acted on, so calling it
    // "blocked before reaching the server" would be a guess stated as
    // a fact — the exact failure this whole change exists to stop.
    const name = (err as { name?: unknown } | null)?.name;
    if (name === 'AbortError' || name === 'TimeoutError') throw err;
    throw new ApiNetworkError(init.method ?? 'GET', url, err);
  }

  if (!res.ok) {
    let body: ApiError | { message: string };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      body = { message: `HTTP ${res.status} ${res.statusText}` };
    }
    throw new ApiClientError(res.status, body);
  }
  return res;
}

// ----------------------------------------------------------------------------
// Sites and capabilities (plan §6.3: "SiteContext owns the authorized
// API list and active site; assignment removal aborts streams, clears
// site caches, and reconciles selection.")
// ----------------------------------------------------------------------------

export async function fetchAssignedSites(): Promise<SiteSummary[]> {
  const res = await apiFetch('/api/v1/sites');
  return (await res.json()) as SiteSummary[];
}

export async function fetchSiteCapabilities(site: SiteId): Promise<SiteCapabilities> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/capabilities`);
  return (await res.json()) as SiteCapabilities;
}

export async function fetchReady(): Promise<ReadyzResponse> {
  const res = await apiFetch('/ready');
  return (await res.json()) as ReadyzResponse;
}

// ----------------------------------------------------------------------------
// Command lifecycle (plan §Command lifecycle)
// ----------------------------------------------------------------------------

export async function dispatchTask(
  site: SiteId,
  body: DispatchTaskRequest,
): Promise<DispatchTaskResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/commands/dispatch_task`, {
    method: 'POST',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as DispatchTaskResponse;
}

export async function controlTask(
  site: SiteId,
  task: TaskId,
  body: TaskControlRequest,
): Promise<TaskControlResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(task.task)}/control`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as TaskControlResponse;
}

// ----------------------------------------------------------------------------
// Task favourites (saved dispatch templates for one-click re-run)
// ----------------------------------------------------------------------------

export async function fetchFavorites(site: SiteId): Promise<TaskFavorite[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/favorites`);
  return (await res.json()) as TaskFavorite[];
}

export async function createFavorite(
  site: SiteId,
  body: CreateTaskFavoriteRequest,
): Promise<TaskFavorite> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/favorites`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as TaskFavorite;
}

export async function deleteFavorite(site: SiteId, favoriteId: string): Promise<void> {
  await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/favorites/${encodeURIComponent(favoriteId)}`,
    { method: 'DELETE' },
  );
}

export async function fetchSupportTickets(site: SiteId): Promise<SupportTicket[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/tickets`);
  const body = (await res.json()) as { tickets: SupportTicket[] };
  return body.tickets;
}

export async function createSupportTicket(
  site: SiteId,
  body: CreateSupportTicketRequest,
): Promise<SupportTicket> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/tickets`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as SupportTicket;
}

export async function fetchSupportTicket(
  site: SiteId,
  ticketId: string,
): Promise<SupportTicketDetail> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/tickets/${encodeURIComponent(ticketId)}`,
  );
  return (await res.json()) as SupportTicketDetail;
}

/**
 * Add a message to a ticket.
 *
 * No `visibility` is sent, deliberately. The endpoint accepts one and
 * defaults it to `internal`, which for a reporter would file their own
 * message against their own ticket and then hide it from them. The
 * server now forces `requester_visible` for anyone outside the triage
 * roles, so sending nothing is both correct and the only thing this app
 * should ever want.
 */
export async function addSupportTicketComment(
  site: SiteId,
  ticketId: string,
  body: string,
): Promise<SupportTicketComment> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/tickets/${encodeURIComponent(ticketId)}/comments`,
    { method: 'POST', body: JSON.stringify({ body }) },
  );
  return (await res.json()) as SupportTicketComment;
}

export async function executeFavorite(
  site: SiteId,
  favoriteId: string,
  body: ExecuteTaskFavoriteRequest,
): Promise<DispatchTaskResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/favorites/${encodeURIComponent(favoriteId)}/execute`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as DispatchTaskResponse;
}

export async function getCommand(site: SiteId, commandId: string): Promise<CommandRecord> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/commands/${encodeURIComponent(commandId)}`,
  );
  return (await res.json()) as CommandRecord;
}

// ----------------------------------------------------------------------------
// Doors (plan §7.3)
// ----------------------------------------------------------------------------

export async function doorCommand(
  site: SiteId,
  door: string,
  body: DoorCommandRequest,
): Promise<DoorCommandResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/doors/${encodeURIComponent(door)}/command`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as DoorCommandResponse;
}

/**
 * Declare a door manually open (`door.declare_open`).
 *
 * NOT a door command: nothing is asked of the door. This writes a
 * record in the site's lifecycle broker that makes
 * `rmf-door-state-mux` synthesize MODE_OPEN, which is what releases a
 * robot waiting at a door RMF's DoorOpen phase will otherwise wait at
 * forever.
 *
 * The TTL that comes back at `command.target.ttl_s` is the GRANTED one
 * and may be smaller than the one sent -- the api-server clamps rather
 * than refusing. Read it back; do not assume.
 */
export async function declareDoorOpen(
  site: SiteId,
  door: string,
  body: DoorOverrideRequest,
): Promise<DoorOverrideResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/doors/${encodeURIComponent(door)}/override`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as DoorOverrideResponse;
}

/**
 * Revoke an override early (`door.revoke_override`).
 *
 * THE IDEMPOTENCY KEY TRAVELS IN THE QUERY STRING, not a body, and
 * this call must send NO body at all. A DELETE with a body is legal
 * and FastAPI would parse one, but bodies on DELETE are the part of
 * HTTP that intermediaries feel free to drop, and the failure mode of
 * a silently-dropped key here is a 422 on the one press that ENDS an
 * override. (`X-Idempotency-Key` still rides as a header via
 * `idempotencyKey`, but the api-server reads the query parameter --
 * the header alone would be a 422.)
 */
export async function revokeDoorOverride(
  site: SiteId,
  door: string,
  idempotencyKey: string,
  expectedWorkerEpoch?: number,
): Promise<DoorOverrideResponse> {
  const params = new URLSearchParams({ idempotency_key: idempotencyKey });
  if (expectedWorkerEpoch !== undefined) {
    params.set('expected_worker_epoch', String(expectedWorkerEpoch));
  }
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/doors/${encodeURIComponent(door)}/override?${params.toString()}`,
    { method: 'DELETE', idempotencyKey },
  );
  return (await res.json()) as DoorOverrideResponse;
}

// ----------------------------------------------------------------------------
// Door write gate (tier 1 `inventory_ok` + tier 2 `armed`, per ADAPTER)
//
// The three types below are declared HERE rather than beside
// `LiftWriteGate` in `v1.ts` because the door gate reached the console
// after that file was split out of this workstream's reach; they are
// contract types like any other and belong next to it when the two
// gates are next touched together.
// ----------------------------------------------------------------------------

/**
 * One door adapter's write gate, exactly as the adapter published it.
 *
 * PER ADAPTER, NOT PER DOOR (decision D1), and the difference is the
 * whole reason `doors` is on the wire: one arm covers every door in
 * that array, so a console that showed this against a single door
 * would be understating the blast radius by however many doors the
 * adapter happens to own. At sg-nomad-01 that is `L4_SLL_Door` AND the
 * sandbox row, from one press.
 *
 * NEVER PERSISTED at the adapter (invariant I1): `write_gate.set_armed`
 * writes to memory and nothing reads an arm intent back out, so every
 * restart comes back disarmed. A console caching this across a session
 * would be showing an arm that no longer exists.
 */
export interface DoorWriteGate {
  readonly schema_version: string;
  readonly ts: number;
  readonly site: SiteId;
  /** Which adapter this gate belongs to; the path segment of the arm
   *  POST, and what makes D1's scoping real when a site grows a second
   *  door adapter. */
  readonly adapter_id: string;
  readonly armed: boolean;
  /** `""` when not armed -- the adapter clears it on disarm. */
  readonly armed_by: string;
  /** `0.0` when not armed. */
  readonly armed_ts: number;
  /**
   * Whether THIS PROCESS has ever been armed. False after a restart,
   * TRUE after a deliberate disarm, which is the only thing that tells
   * those two apart on the wire -- both otherwise read
   * `armed: false, armed_by: ""`. The api-server's restart alert is
   * built on it (decision D4); this console does not re-derive one.
   */
  readonly armed_since_start: boolean;
  /** `""` if nobody has disarmed since this process started. */
  readonly disarmed_by: string;
  /** Tier 1, and the adapter's alone to decide (invariant I3): did the
   *  vendor's device list resolve into addressable doors. */
  readonly inventory_ok: boolean;
  /** The adapter's own sentence about why tier 1 failed. Rendered
   *  rather than summarised, for the reason the lift gate's `detail`
   *  is: which device is missing is the only part an engineer can act
   *  on. */
  readonly inventory_detail: string;
  /**
   * True when the adapter was NOT deployed with `live: true`.
   *
   * The dimension the lift gate does not have, and the one that can
   * make an armed console lie. `translate.door_mode` returns
   * MODE_OFFLINE while `shadow` holds no matter what `armed` says, and
   * `WriteGate.check` refuses every opening action with a SHADOW MODE
   * error that reaches no browser -- so an operator arms, sees
   * "armed", presses Open and watches nothing happen, with no
   * explanation anywhere. Deploy-time only: `live` is a reviewed git
   * change, never a toggle (decision D2).
   */
  readonly shadow: boolean;
  readonly transport: string;
  readonly link_ok: boolean;
  /** Every RMF door name this adapter owns. What the confirm dialog
   *  reads back, so an operator can never discover the scope of their
   *  own arm afterwards. */
  readonly doors: readonly string[];
  /** The arm request this state resulted from, `""` if none. The only
   *  way a caller can tell its own arm from a concurrent one, which is
   *  why the api-server mints it. */
  readonly request_id: string;
  /**
   * Set by the COLLECTION read only: this gate is too old to present as
   * current.
   *
   * A stale entry is RETURNED rather than dropped, because omitting it
   * would render as "this site has no such adapter" -- the same
   * sentence a site with no door hardware gets, said about an adapter
   * that may well be armed and driving doors. Render it as unreadable,
   * never as absent and never as off.
   */
  readonly stale?: boolean;
  /** Age of the reading in ms, `null` when the sample carried no
   *  datable stamp (which is itself treated as stale). */
  readonly age_ms?: number | null;
  readonly process_start_ts: number;
}

/**
 * An arm/disarm decision on its way to a door adapter.
 *
 * The console-owned half of the `door_arm` contract document
 * (`adapters-door/contracts/nexsoss/door_arm.schema.json`), which
 * travels SPA -> api-server -> worker -> ROS `door_arm_requests` ->
 * adapter and is validated against that one schema on the way.
 *
 * FOUR OF THE DOCUMENT'S FIELDS ARE NOT HERE, and the api-server mints
 * every one of them: `actor` (from the verified token -- a browser
 * that could name its own actor could sign someone else's name to an
 * armed door), `request_id`, `ts` (stamped at authorisation, not at
 * publish) and `schema_version`. `adapter_id` is not here either: it
 * is the path segment, so a body naming a different adapter than the
 * URL is not a state this type can express.
 */
export interface DoorArmRequest {
  /** Absolute, never a toggle: a redelivered request must be able to
   *  land twice without flipping the gate back. */
  readonly armed: boolean;
  /**
   * The two-key turn, and meaningful only when arming. Omitted
   * entirely on a disarm rather than sent as `false` (invariant I2) --
   * a disarm during an incident must never be refusable on a missing
   * flag, and a field on the wire is a field some future reader starts
   * checking.
   */
  readonly confirmed?: boolean;
  readonly reason?: string;
  readonly idempotency_key: string;
}

/**
 * What the api-server can say immediately: the request was authorised
 * and handed to the worker.
 *
 * `requested_armed`, NOT `armed`. The adapter decides -- it refuses an
 * arm whose inventory has not resolved, and it does so on its own log
 * and its next gate document, never in this response. Rendering a
 * request as though it were the live flag is the bug the lift panel
 * was built to stop repeating.
 */
export interface DoorArmResponse {
  readonly command: CommandRecord;
  readonly request_id: string;
  readonly requested_armed: boolean;
  // WHICH ADAPTER THE SERVER ACTUALLY ACTED ON. Idempotency is scoped to
  // (site, caller, key) and NOT to the adapter, so a client that reuses
  // one key across two adapters gets the FIRST one's stored command
  // back with HTTP 200 -- indistinguishable from a fresh arm of the
  // second unless the reply names its subject. The api-server added
  // this field for exactly that case; dropping it here throws away the
  // only evidence the console has.
  readonly adapter_id: string;
  // True when this 200 is a replay of a stored command rather than a
  // new publish. Nothing was sent to the adapter.
  readonly replayed: boolean;
}

/**
 * Every door adapter's write gate at this site, one entry per adapter.
 *
 * A COLLECTION, deliberately, even though sg-nomad-01 has exactly one
 * door adapter today. The panel enumerates what comes back rather than
 * assuming the count, because the day a second adapter is deployed is
 * also the day "the door gate" stops meaning one thing, and a console
 * that had hardcoded the first entry would silently arm the wrong
 * adapter's doors.
 *
 * THE NULL IS THE POINT, and it is the same null `fetchLiftWriteGate`
 * returns: a 404 here is a deployment FACT -- this api-server has no
 * door write-gate read path -- and not a failure. Left as a thrown
 * error it bought nothing and cost TanStack's retries times the
 * fifteen-second poll, per open tab, forever, and at the end of them
 * the console still had nothing truer to say. Every OTHER status still
 * throws: "we could not read it" and "there is nothing here to read"
 * are different sentences and the panel renders them differently.
 */
/**
 * One door's health, as the adapter knows it and `current_mode` cannot
 * say it.
 *
 * WHY THIS EXISTS. A door's `state` comes from `current_mode`, a single
 * enum on which the adapter must report `offline` for SEVEN different
 * reasons: the door is not in the vendor inventory, the vendor link is
 * down, the controller's own `status` says offline, the sample is
 * stale, the door has never been sampled, writes are disarmed, or the
 * adapter is in shadow mode. The console showed one value for all
 * seven, so a dead controller and a disarmed adapter looked identical
 * — and they need opposite actions from an operator.
 *
 * `online` IS TRI-STATE AND MUST STAY ONE. `true`, `false`, or `null`
 * for "unknown". Null covers two real cases: a transport that carries
 * no such signal (MEGAZO's MQTT heartbeat has no `status` field), and a
 * health document that has aged out — because "we stopped hearing" is
 * not "the controller is dead". Rendering null as offline would make
 * one dead adapter look like a building full of dead doors.
 */
export interface DoorHealth {
  readonly door: string;
  readonly adapter_id: string;
  /** `null` is UNKNOWN. Never render it as offline. */
  readonly online: boolean | null;
  /** The physical door position with every trust condition ignored —
   *  what the door really is, as against what RMF is told. Shown in the
   *  drawer only: a list row reading `offline` and `open` at once is
   *  read as a bug by anyone who has not been told the difference. */
  readonly observed: string;
  /** What RMF is actually told. The two differing is the signal. */
  readonly published: string;
  /** How old the DOOR's reading is. Distinct from `age_ms`, which is
   *  how old the ADAPTER's document is: an adapter can be publishing
   *  promptly about a door it has not heard from in minutes. */
  readonly sample_age_ms: number | null;
  readonly sample_source: string;
  readonly in_inventory: boolean;
  /** The mode a command is currently driving this door toward. */
  readonly commanding_to: string | null;
  /** A command that did not take inside its window. Carried so an
   *  operator can be told — nothing retries on it. */
  readonly command_failed: Record<string, unknown> | null;
  readonly age_ms: number | null;
  readonly stale: boolean;
}

/**
 * Per-door health for every door adapter at this site.
 *
 * `null` is a settled 404 (an api-server with no health read path),
 * `[]` is a readable answer meaning nothing publishes health here, and
 * a rejected promise is "we could not read it". The door list must keep
 * all three apart, exactly as the write gate does.
 */
export async function fetchDoorHealth(site: SiteId): Promise<DoorHealth[] | null> {
  try {
    const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/doors/health`);
    const body = (await res.json()) as unknown;
    // Same rule as the gate read beside this: `[]` survives as `[]`,
    // and anything that is not a list is thrown rather than collapsed
    // into one — "no adapter publishes health here" is a claim about a
    // building and must not be manufactured from a response the console
    // did not understand.
    if (!Array.isArray(body)) {
      throw new Error('the door health read returned something other than a list');
    }
    return body as DoorHealth[];
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

export async function fetchDoorWriteGates(site: SiteId): Promise<DoorWriteGate[] | null> {
  try {
    const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/doors/write-gate`);
    const body = (await res.json()) as unknown;
    // An empty array is a LEGITIMATE answer -- the read succeeded and
    // this site has no door adapter -- and must survive as `[]`.
    //
    // Anything that is not a list is NOT that answer. Collapsing it to
    // `[]` would render "the gate read succeeded and no door adapter at
    // this site publishes one", which is a settled claim about a
    // building, manufactured out of a response the console did not
    // understand. Throw instead: "we could not read it" is the panel's
    // other sentence and it already has one.
    if (!Array.isArray(body)) {
      throw new Error('the door write-gate read returned something other than a list of gates');
    }
    return body as DoorWriteGate[];
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Arm or disarm ONE door adapter (`door.arm_writes`).
 *
 * `adapter_id` is a path segment rather than a body field so the thing
 * being armed is the thing the URL names; there is no way to send a
 * body that disagrees with it.
 *
 * What comes back is a `CommandRecord`, not the gate. The adapter's
 * accept or refusal lands on its own log and its next
 * `door_write_gate` document; neither reaches this response, so
 * callers must not treat a 200 as "armed".
 */
export async function armDoorWrites(
  site: SiteId,
  adapterId: string,
  body: DoorArmRequest,
): Promise<DoorArmResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/doors/adapters/${encodeURIComponent(adapterId)}/write-gate/arm`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as DoorArmResponse;
}

// ----------------------------------------------------------------------------
// Lifts (server-owned sessions — plan §Command lifecycle)
// ----------------------------------------------------------------------------

export async function acquireLiftSession(
  site: SiteId,
  body: AcquireLiftSessionRequest,
): Promise<AcquireLiftSessionResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/lift/sessions`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as AcquireLiftSessionResponse;
}

/** Extends `expires_at_ms` on an existing session without minting a
 *  new session_id — see `lift_sessions.py::renew`'s docstring for
 *  why a fresh id per "refresh" would break the worker's lock. */
export async function renewLiftSession(
  site: SiteId,
  sessionId: string,
): Promise<AcquireLiftSessionResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lift/sessions/${encodeURIComponent(sessionId)}/renew`,
    { method: 'POST' },
  );
  return (await res.json()) as AcquireLiftSessionResponse;
}

/**
 * Release this PANEL'S LEASE on a lift. Nothing reaches the lift.
 *
 * NAME COLLISION WORTH KNOWING ABOUT. Two unrelated things in this
 * codebase are called "releasing a lift session":
 *
 *   1. THIS -- the nexsoss panel lease, a 60 s exclusivity token so two
 *      operators cannot drive one car at once. Releasing it flips a
 *      database row and sends NOTHING to RMF, the worker or the lift.
 *      Called on drawer unmount.
 *   2. `liftEndSession` -- ending the RMF session that actually holds
 *      the car, which returns it to public service with whatever is
 *      inside it.
 *
 * Confusing them in either direction is bad: believing this one released
 * a car it did not, or believing that one merely dropped a lease.
 */
export async function releaseLiftSession(
  site: SiteId,
  body: ReleaseLiftSessionRequest,
): Promise<void> {
  await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lift/sessions/${encodeURIComponent(body.session_id)}`,
    { method: 'DELETE' },
  );
}

export async function liftCommand(
  site: SiteId,
  lift: string,
  body: LiftCommandRequest,
): Promise<LiftCommandResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/command`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as LiftCommandResponse;
}

/**
 * Recover one car. See `LiftAbortRequest` for why this is a separate
 * route rather than another `action` on `liftCommand` — it takes no lift
 * session, because the ride it ends is usually somebody else's.
 */
export async function liftAbort(
  site: SiteId,
  lift: string,
  body: LiftAbortRequest,
): Promise<LiftAbortResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/abort`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      idempotencyKey: body.idempotency_key,
    },
  );
  return (await res.json()) as LiftAbortResponse;
}


/**
 * "Release lift" — advance a stalled session by one wait.
 *
 * Takes no lift session, like the abort: the stalled ride belongs to
 * somebody else, usually a robot.
 */
export async function liftAdvance(
  site: SiteId,
  lift: string,
  body: LiftAdvanceRequest,
): Promise<LiftVerbResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/advance`,
    { method: 'POST', body: JSON.stringify(body), idempotencyKey: body.idempotency_key },
  );
  return (await res.json()) as LiftVerbResponse;
}

/** "Recover stuck robot" — lift session ended, car released, task cancelled. */
export async function liftRecover(
  site: SiteId,
  lift: string,
  body: LiftRecoverRequest,
): Promise<LiftVerbResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/recover`,
    { method: 'POST', body: JSON.stringify(body), idempotencyKey: body.idempotency_key },
  );
  return (await res.json()) as LiftVerbResponse;
}

/**
 * "Request Floor" — an operator-owned one-shot call of the car.
 *
 * NOT `liftCommand(request_destination)`. That published an AGV-mode
 * request to rmf_lift_supervisor under `nexsoss-worker-<lift>`, which the
 * supervisor re-presented at 1 Hz forever and the adapter kept re-opening
 * every ~36 s (Hotel NoMad, 2026-08-29). The api-server answers 410 for
 * it now. This route goes to the lift-session adapter directly, is never
 * retried, and answers with a per-request verdict the drawer correlates
 * on `request_id`.
 */
export async function liftCall(
  site: SiteId,
  lift: string,
  body: LiftManualCallRequest,
): Promise<LiftVerbResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/call`,
    { method: 'POST', body: JSON.stringify(body), idempotencyKey: body.idempotency_key },
  );
  return (await res.json()) as LiftVerbResponse;
}

/** "Cancel call" — end the operator-owned session. Admitted from any
 *  phase, including while the call is still in flight. */
export async function liftClose(
  site: SiteId,
  lift: string,
  body: LiftManualCloseRequest,
): Promise<LiftVerbResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/close`,
    { method: 'POST', body: JSON.stringify(body), idempotencyKey: body.idempotency_key },
  );
  return (await res.json()) as LiftVerbResponse;
}

/** "End RMF session" — the teardown. Hands the car back as it is. */
export async function liftEndSession(
  site: SiteId,
  lift: string,
  body: LiftEndSessionRequest,
): Promise<LiftVerbResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/lifts/${encodeURIComponent(lift)}/end_session`,
    { method: 'POST', body: JSON.stringify(body), idempotencyKey: body.idempotency_key },
  );
  return (await res.json()) as LiftVerbResponse;
}

// ----------------------------------------------------------------------------
// Lift write gate (two-tier: validation, then arming)
//
// Site-scoped and SINGULAR (`/lift/...`, like `/lift/sessions`) rather
// than under `/lifts/{lift}/...`: the bridge holds one gate for the
// whole site (invariant I6), and a per-lift URL would promise a
// granularity nothing downstream can honour.
// ----------------------------------------------------------------------------

/**
 * Read the site's write gate — or `null` when this deployment does not
 * serve one at all.
 *
 * THE NULL IS THE POINT, and it is not a convenience. A 404 on this
 * path is a deployment FACT, not a failure: it says the api-server
 * this console is talking to has no read path for arm state, which is
 * exactly the shape every deployment was in when this endpoint was
 * first called. Left as a thrown error it bought nothing and cost a
 * great deal — TanStack's three default retries plus the fifteen
 * second poll made four 404s every fifteen seconds, per open Lifts
 * tab, indefinitely, and at the end of them the console still had
 * nothing truer to say than it had at the start.
 *
 * Every OTHER status still throws, deliberately. A 500, a 502 from
 * Envoy or a dropped connection all mean "we could not read it",
 * which is a different sentence from "there is nothing here to read",
 * and the verdict rendered over this says which of the two it is. A
 * blanket catch here would collapse them and let a broken api-server
 * pass for a deployment that simply predates the feature.
 */
export async function fetchLiftWriteGate(site: SiteId): Promise<LiftWriteGate | null> {
  try {
    const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/lifts/write_gate`);
    return (await res.json()) as LiftWriteGate;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Arm or disarm dispatching writes for the site.
 *
 * The console never holds MQTT credentials and never reaches the
 * broker: this POST is the head of the chain api-server -> worker
 * (ROS `lift_arm_requests`) -> cloud lift adapter -> `site/cmd/arm`,
 * where the cloud lift adapter holds the only identity permitted to
 * write lift topics (invariant I7).
 *
 * Site-level under `/lifts`, with no `{lift}` segment and no lift
 * session, because the gate is one flag for the whole bridge
 * (invariant I6) and there is no car here to hold.
 *
 * What comes back is a `CommandRecord`, not the gate. The bridge's own
 * accept/refuse arrives on its MQTT ack topic and its new gate state
 * on the retained `site/validation` document; neither reaches this
 * response, so callers must not treat a 200 as "armed".
 */
export async function armLiftWrites(site: SiteId, body: LiftArmRequest): Promise<LiftArmResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/lifts/arm`, {
    method: 'POST',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as LiftArmResponse;
}

// ----------------------------------------------------------------------------
// Robots (plan §7.2)
// ----------------------------------------------------------------------------

export async function commissionRobot(
  site: SiteId,
  body: RobotCommissionRequest,
): Promise<RobotCommissionResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/robots/commission`, {
    method: 'POST',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as RobotCommissionResponse;
}

/**
 * Turn automatic charging (RMF's `idle_behavior` flag) on or off for
 * one robot.
 *
 * PUT, not POST: the body is the whole desired state for that robot,
 * and sending it twice must leave the same one row rather than stack
 * up policies. The idempotency key still travels — it is what makes a
 * retried request return the ORIGINAL command record instead of
 * publishing a second command to RMF.
 *
 * A 409 here is expected and carries a human sentence: the robot is
 * inside a lift car (cancelling its charge trip there would strand it
 * holding the lift session), or another command was sent to it seconds
 * ago. Render `detail.message` rather than a generic failure.
 */
export async function setRobotIdleBehavior(
  site: SiteId,
  body: RobotIdleBehaviorRequest,
): Promise<RobotIdleBehaviorResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/robots/idle_behavior`, {
    method: 'PUT',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as RobotIdleBehaviorResponse;
}

/**
 * "The bag is empty, carry on" (`robot.confirm_dustbag`).
 *
 * Addresses the ROBOT. `taskId` is sent only when the caller knows it
 * -- OMITTED rather than nulled, because the worker's target for this
 * capability omits the key entirely when the caller supplied none, and
 * a `null` on the wire is a key some future reader starts checking.
 *
 * `robot.site` must equal the path site or the api-server answers 400;
 * both come from the same active-site id here, so that can only fire
 * if a caller passes a robot from another site.
 */
export async function confirmDustbag(
  site: SiteId,
  body: RobotConfirmDustbagRequest,
): Promise<RobotConfirmDustbagResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/robots/confirm_dustbag`, {
    method: 'POST',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as RobotConfirmDustbagResponse;
}

/**
 * "I have confirmed which floor this robot is on" (`robot.fire_relocalize`).
 *
 * Releases the hold the fleet adapter takes when a fire alarm clears.
 * Addresses the ROBOT and carries NO task id: the hold outlives whatever
 * task was running when the alarm sounded, and the adapter re-invokes
 * `execute_action` from scratch on resume, so there is no per-task latch
 * for an id to key.
 *
 * `robot.site` must equal the path site or the api-server answers 400.
 */
export async function fireRelocalize(
  site: SiteId,
  body: RobotFireRelocalizeRequest,
): Promise<RobotFireRelocalizeResponse> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/robots/fire_relocalize`, {
    method: 'POST',
    body: JSON.stringify(body),
    idempotencyKey: body.idempotency_key,
  });
  return (await res.json()) as RobotFireRelocalizeResponse;
}

/** Every robot at this site whose automatic charging is currently
 *  switched off. Readable in every role — it is the only source for
 *  the drawer's "until HH:MM" countdown, which is desired state and
 *  does not appear in any fleet state. */
export async function fetchRobotIdlePolicies(site: SiteId): Promise<RobotIdlePolicy[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/robots/idle_behavior`);
  return (await res.json()) as RobotIdlePolicy[];
}

// ----------------------------------------------------------------------------
// Building map (read-through to RocksDB cache; plan §Map contract)
// ----------------------------------------------------------------------------

export async function fetchBuildingMap(
  site: SiteId,
  level = 'L1',
  revision?: string,
): Promise<BuildingMapSnapshot | null> {
  const qs = new URLSearchParams({ level });
  if (revision) qs.set('revision', revision);
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/building_map?${qs.toString()}`,
    // The largest response this API serves, and effectively static: a new
    // map arrives under a NEW revision, and the SSE
    // `building_map_revision` notification is what tells us to refetch.
    // Opting out of `no-store` lets the browser revalidate with
    // If-None-Match and take a 304 instead of several hundred KB.
    //
    // Revalidation is handled BY THE BROWSER: on a 304 it serves the
    // cached body and this code sees an ordinary 200. There is
    // deliberately no `res.status === 304` branch here -- a bare 304 has
    // no body, and returning null for it would be read by
    // `fetchAssembledBuildingMap` as "this site has no map" and blank the
    // display. Letting `res.json()` throw on that impossible-in-practice
    // case keeps the failure loud and lets TanStack Query hold the last
    // good snapshot.
    { cache: 'default' },
  );
  return (await res.json()) as BuildingMapSnapshot | null;
}

/** Level names discoverable for this site (plan §Phase 4D level
 *  switcher) — used to populate the level picker before any specific
 *  level's snapshot has been fetched. */
export async function fetchBuildingMapLevels(site: SiteId): Promise<string[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/building_map/levels`);
  return (await res.json()) as string[];
}

// ----------------------------------------------------------------------------
// Door / lift registries (static geometry; plan §2 "registry never
// overwritten by state-only updates"). These are the building's
// INVENTORY, read by `doors.py`/`lifts.py` from the worker's
// `doors_registry`/`lifts_registry` keys, and they answer with real
// entries -- sg-nomad-01's held `L4_SLL_Door` throughout the morning
// its MEGAZO adapter published no state at all. That is why
// `registry-merge.ts` lets the registry decide which devices exist:
// a console driven by live state alone showed no door. (An older
// api-server may still answer 404 here, which the callers degrade to
// today's live-only list.) The mock backs these with real per-site
// geometry.
// ----------------------------------------------------------------------------

export async function fetchDoorsRegistry(site: SiteId): Promise<DoorRegistry[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/doors`);
  return (await res.json()) as DoorRegistry[];
}

export async function fetchLiftsRegistry(site: SiteId): Promise<LiftRegistry[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/lifts`);
  return (await res.json()) as LiftRegistry[];
}

export async function fetchPlaces(site: SiteId): Promise<PlaceRegistry[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/places`);
  return (await res.json()) as PlaceRegistry[];
}

export async function fetchTaskCatalog(site: SiteId): Promise<TaskCatalogEntry[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/task_catalog`);
  return (await res.json()) as TaskCatalogEntry[];
}

// ----------------------------------------------------------------------------
// Twin reads (3D view only)
// ----------------------------------------------------------------------------

/**
 * Per-zone cleaning freshness. See `twin.ts` for what each status means and
 * why the distinctions matter — in particular that `trailVerified: null` is
 * "could not check", never "did not happen".
 */
export async function fetchZoneFreshness(
  site: SiteId,
  days = 30,
): Promise<ZoneFreshnessResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/zones/freshness?days=${days}`,
  );
  return (await res.json()) as ZoneFreshnessResponse;
}

/**
 * A robot's recorded pose trail over a window. Stride-sampled server-side and
 * never truncated: `stride > 1` means the whole window is present, thinner.
 */
export async function fetchRobotTrail(
  site: SiteId,
  robot: string,
  fromMs: number,
  toMs: number,
  maxPoints = 2000,
): Promise<RobotTrail> {
  const qs = `from_ms=${fromMs}&to_ms=${toMs}&max_points=${maxPoints}`;
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/robots/${encodeURIComponent(robot)}/trail?${qs}`,
  );
  return (await res.json()) as RobotTrail;
}

/**
 * Every robot's last recorded real pose, for the ghosts (twin slice 1,
 * plan W3 step 8). A 404 is `{ supported: false }` -- an api-server that
 * predates the route -- and never an error: the caller falls back to
 * session-only ghosts. Every other failure is thrown as usual.
 */
export async function fetchRobotLastPoses(
  site: SiteId,
  sinceMs?: number,
): Promise<RobotLastPosesResult> {
  const qs = sinceMs !== undefined ? `?since_ms=${Math.floor(sinceMs)}` : '';
  try {
    const res = await apiFetch(
      `/api/v1/sites/${encodeURIComponent(site)}/robots/last_poses${qs}`,
    );
    const body = (await res.json()) as RobotLastPosesResponse;
    return { supported: true, ...body };
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return { supported: false };
    throw err;
  }
}

// ----------------------------------------------------------------------------
// History / audit (paginated, plan §3.1)
// ----------------------------------------------------------------------------

export async function fetchTaskLog(
  site: SiteId,
  task: TaskId,
  cursor?: string,
  limit = 100,
): Promise<PaginatedTaskLog> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(task.task)}/log?${qs.toString()}`,
  );
  return (await res.json()) as PaginatedTaskLog;
}

/**
 * A page of FINISHED tasks from the durable record, newest finish first --
 * the Tasks tab's older view (`api/history.py::list_task_completions`).
 *
 * `beforeMs` (exclusive) is where the first page starts; `cursor` is the
 * previous page's `next_cursor`, passed back verbatim. Both may be sent
 * together: the cursor continues the walk inside the bound.
 */
export async function fetchTaskCompletions(
  site: SiteId,
  opts: { beforeMs?: number; afterMs?: number; cursor?: string; limit?: number } = {},
): Promise<PaginatedTaskCompletions> {
  const qs = new URLSearchParams({ limit: String(opts.limit ?? 50) });
  if (opts.beforeMs !== undefined) qs.set('before_ms', String(Math.floor(opts.beforeMs)));
  // Inclusive floor (api-server calendar-lanes 2026-10-30). An older
  // server ignores the unknown parameter and keeps paging past it, so a
  // caller that passes it must still stop on its own at the floor.
  if (opts.afterMs !== undefined) qs.set('after_ms', String(Math.max(0, Math.floor(opts.afterMs))));
  if (opts.cursor) qs.set('cursor', opts.cursor);
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/history/task_completions?${qs.toString()}`,
  );
  return (await res.json()) as PaginatedTaskCompletions;
}

/** The most ids `GET .../history/task_distances` takes in one request;
 *  more is a 422 `too_many_task_ids` (`history.py`). */
export const TASK_DISTANCE_IDS_PER_REQUEST = 200;

/**
 * The latest distance totals for the named tasks -- the Tasks tab's
 * catch-up read for its live rows (`useTaskDistanceCatchUp`). An id with
 * no totals on record is simply absent from the answer.
 *
 * NULL MEANS "THIS API-SERVER DOES NOT KNOW THE ROUTE": a 404 is what one
 * older than 0.6.41 answers, and the rows then show a dash -- the live
 * stream still fills in tasks that run from here on. Any other failure
 * throws, for the caller to leave the rows unread and try again later.
 */
export async function fetchTaskDistances(
  site: SiteId,
  taskIds: readonly string[],
): Promise<TaskDistanceRecord[] | null> {
  const qs = new URLSearchParams();
  for (const id of taskIds.slice(0, TASK_DISTANCE_IDS_PER_REQUEST)) qs.append('task_id', id);
  try {
    const res = await apiFetch(
      `/api/v1/sites/${encodeURIComponent(site)}/history/task_distances?${qs.toString()}`,
    );
    const body = (await res.json()) as TaskDistanceList;
    return Array.isArray(body?.entries) ? body.entries : [];
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

/**
 * The dispatch request behind a task, or null when there is none on file.
 *
 * 404 IS A LEGITIMATE ANSWER, not an error: an RMF-originated task (the
 * low-battery charge retreat, the idle-behaviour park) was never dispatched
 * from this platform, and so is a task read from an api-server that
 * predates the endpoint. Both must read as "not on file" rather than as a
 * failure.
 */
export async function fetchTaskRequest(
  site: SiteId,
  task: string,
): Promise<TaskRequestRecord | null> {
  try {
    const res = await apiFetch(
      `/api/v1/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(task)}/request`,
    );
    return (await res.json()) as TaskRequestRecord;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

export async function fetchAudit(
  site: SiteId,
  cursor?: string,
  limit = 100,
  actorSub?: string,
): Promise<PaginatedAudit> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  if (actorSub) qs.set('actor_sub', actorSub);
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/audit?${qs.toString()}`);
  return (await res.json()) as PaginatedAudit;
}

/** What `POST …/audit/role-preview` records (api-server `api/audit.py`). */
export type RolePreviewAuditAction = 'enter' | 'exit' | 'sign_out' | 'dropped';

/**
 * Record entering or leaving a "View as role" preview in the site's
 * audit trail. The ONE non-telemetry write the preview's write block
 * lets through (`isPreviewExemptWrite`). The server stamps the actor
 * and the time; the body names only what happened and which role.
 *
 * `keepalive` so the sign-out record survives the navigation to the
 * identity provider that immediately follows it.
 */
export async function recordRolePreviewAudit(
  site: SiteId,
  action: RolePreviewAuditAction,
  previewedRole: string,
): Promise<void> {
  await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/audit/role-preview`, {
    method: 'POST',
    body: JSON.stringify({ action, previewed_role: previewedRole }),
    keepalive: true,
  });
}

// ----------------------------------------------------------------------------
// Recurring task schedules
//
// Occurrences are virtual: `fetchScheduleOccurrences` expands the rules
// server-side for the requested window. Per-occurrence edits address an
// occurrence by its ORIGINAL start (what the rule produced), not by
// where it currently sits, so an override stays attached to the same
// occurrence after it has been moved.
// ----------------------------------------------------------------------------

// --- Planned maintenance windows --------------------------------------------

/** Windows OVERLAPPING the range, not contained by it: a week-long
 *  window is the one most worth seeing on a Tuesday, and a
 *  contained-only query would hide exactly the biggest ones. */
export async function fetchMaintenanceWindows(
  site: SiteId,
  fromMs: number,
  toMs: number,
): Promise<MaintenanceWindow[]> {
  const qs = new URLSearchParams({ from_ms: String(fromMs), to_ms: String(toMs) });
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/maintenance?${qs.toString()}`,
  );
  return (await res.json()) as MaintenanceWindow[];
}

export async function createMaintenanceWindow(
  site: SiteId,
  body: CreateMaintenanceWindowRequest,
): Promise<MaintenanceWindow> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/maintenance`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as MaintenanceWindow;
}

export async function deleteMaintenanceWindow(
  site: SiteId,
  windowId: string,
): Promise<void> {
  await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/maintenance/${encodeURIComponent(windowId)}`,
    { method: 'DELETE' },
  );
}

// -- Lift rules ----------------------------------------------------------------

function liftRulesUrl(site: SiteId, tail: string): string {
  return `/api/v1/sites/${encodeURIComponent(site)}/lifts/${tail}`;
}

/** Every lift's rules, as decided and as the adapters report applying them.
 *  503 `lift_rules_not_reporting` when no adapter has reported recently. */
export async function fetchLiftRules(site: SiteId): Promise<LiftRulesView> {
  const res = await apiFetch(liftRulesUrl(site, 'rules'));
  return (await res.json()) as LiftRulesView;
}

/** 409 `lift_rule_cuts_off`, with `levels`, until `confirm_cut_off` is sent. */
export async function putLiftRuleHold(
  site: SiteId,
  lift: string,
  scope: LiftRuleScope,
  body: LiftRuleHoldRequest,
): Promise<LiftRuleLiftView> {
  const res = await apiFetch(
    liftRulesUrl(site, `${encodeURIComponent(lift)}/rules/${scope}/hold`),
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return (await res.json()) as LiftRuleLiftView;
}

export async function deleteLiftRuleHold(
  site: SiteId,
  lift: string,
  scope: LiftRuleScope,
): Promise<LiftRuleLiftView> {
  const res = await apiFetch(
    liftRulesUrl(site, `${encodeURIComponent(lift)}/rules/${scope}/hold`),
    { method: 'DELETE' },
  );
  return (await res.json()) as LiftRuleLiftView;
}

/** 409 when the scope has not tripped. */
export async function resetLiftRuleBreaker(
  site: SiteId,
  lift: string,
  scope: LiftRuleScope,
): Promise<LiftRuleLiftView> {
  const res = await apiFetch(
    liftRulesUrl(site, `${encodeURIComponent(lift)}/rules/${scope}/breaker-reset`),
    { method: 'POST' },
  );
  return (await res.json()) as LiftRuleLiftView;
}

export async function fetchLiftRuleWindows(site: SiteId): Promise<LiftRuleWindow[]> {
  const res = await apiFetch(liftRulesUrl(site, 'rule-windows'));
  return (await res.json()) as LiftRuleWindow[];
}

/** The runs overlapping the range, at most 62 days of it. */
export async function fetchLiftRuleOccurrences(
  site: SiteId,
  fromMs: number,
  toMs: number,
): Promise<LiftRuleOccurrencePage> {
  const qs = new URLSearchParams({ from_ms: String(fromMs), to_ms: String(toMs) });
  const res = await apiFetch(liftRulesUrl(site, `rule-windows/occurrences?${qs.toString()}`));
  return (await res.json()) as LiftRuleOccurrencePage;
}

export async function createLiftRuleWindow(
  site: SiteId,
  body: CreateLiftRuleWindowRequest,
): Promise<LiftRuleWindow> {
  const res = await apiFetch(liftRulesUrl(site, 'rule-windows'), {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as LiftRuleWindow;
}

export async function deleteLiftRuleWindow(site: SiteId, windowId: string): Promise<void> {
  await apiFetch(liftRulesUrl(site, `rule-windows/${encodeURIComponent(windowId)}`), {
    method: 'DELETE',
  });
}

/** Skip one run of a repeating window, as Skip does for a task schedule. */
export async function skipLiftRuleOccurrence(
  site: SiteId,
  windowId: string,
  occurrenceStartMs: number,
): Promise<LiftRuleWindow> {
  const res = await apiFetch(liftRulesUrl(site, `rule-windows/${encodeURIComponent(windowId)}/exdates`), {
    method: 'POST',
    body: JSON.stringify({ occurrence_start_ms: occurrenceStartMs }),
  });
  return (await res.json()) as LiftRuleWindow;
}

/** The Month view's plan check (api-server 0.6.55): the site-calendar days
 *  `fromDate` to `toDate` inclusive, at most 62 of them. Throws like every
 *  read here -- a 404 is an api-server without the route, which the Month
 *  view says in words. */
export async function fetchPlanCheck(
  site: SiteId,
  fromDate: string,
  toDate: string,
  signal?: AbortSignal,
): Promise<PlanCheck> {
  const qs = new URLSearchParams({ from_date: fromDate, to_date: toDate });
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/calendar/plan-check?${qs.toString()}`,
    signal ? { signal } : {},
  );
  return (await res.json()) as PlanCheck;
}

export async function fetchSchedules(site: SiteId): Promise<TaskSchedule[]> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/schedules`);
  return (await res.json()) as TaskSchedule[];
}

export async function fetchScheduleOccurrences(
  site: SiteId,
  fromMs: number,
  toMs: number,
): Promise<ScheduleOccurrence[]> {
  const qs = new URLSearchParams({ from_ms: String(fromMs), to_ms: String(toMs) });
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/occurrences?${qs.toString()}`,
  );
  return (await res.json()) as ScheduleOccurrence[];
}

export async function createSchedule(
  site: SiteId,
  body: CreateTaskScheduleRequest,
): Promise<TaskSchedule> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/schedules`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return (await res.json()) as TaskSchedule;
}

/** PATCH a schedule. A timing change carries `apply_from_ms` (see
 *  `UpdateTaskScheduleRequest`); send only the fields the operator
 *  actually changed, because the server reads any difference in `rrule`,
 *  `dtstart_ms` or `timezone` as a re-anchor. */
export async function updateSchedule(
  site: SiteId,
  scheduleId: string,
  body: UpdateTaskScheduleRequest,
): Promise<TaskSchedule> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
  return (await res.json()) as TaskSchedule;
}

/**
 * What `updateSchedule` would do with the same body, without doing it
 * (api-server 0.6.54, `POST .../preview`). Same gates and validation as
 * the PATCH — a 400 here is the 400 the save would get — and nothing is
 * written or audited.
 *
 * A POST only because the body is a whole PATCH; it changes nothing. It
 * is still a POST to the role-preview write gate (`assertWriteAllowed`),
 * so "View as" refuses it like any write — `useScheduleUpdatePreview`
 * does not ask while a preview is on.
 *
 * `signal` lets a live preview abandon the request a newer keystroke
 * has made moot.
 */
export async function previewScheduleUpdate(
  site: SiteId,
  scheduleId: string,
  body: UpdateTaskScheduleRequest,
  signal?: AbortSignal,
): Promise<ScheduleUpdatePreview> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}/preview`,
    { method: 'POST', body: JSON.stringify(body), ...(signal ? { signal } : {}) },
  );
  return (await res.json()) as ScheduleUpdatePreview;
}

export async function deleteSchedule(site: SiteId, scheduleId: string): Promise<void> {
  await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}`,
    { method: 'DELETE' },
  );
}

/** Skip one occurrence — Google Calendar's "delete this event". */
export async function addScheduleExdate(
  site: SiteId,
  scheduleId: string,
  originalStartMs: number,
): Promise<TaskSchedule> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}/exdates`,
    { method: 'POST', body: JSON.stringify({ original_start_ms: originalStartMs }) },
  );
  return (await res.json()) as TaskSchedule;
}

/** Move or re-payload one occurrence — "edit this event only". */
export async function putScheduleOverride(
  site: SiteId,
  scheduleId: string,
  originalStartMs: number,
  body: ScheduleOccurrenceOverride,
): Promise<void> {
  await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}` +
      `/overrides/${encodeURIComponent(String(originalStartMs))}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

/** What "Start now" answered: the occurrence it dispatched and the
 *  command record (`api/schedules.py::run_occurrence_now`). */
export interface RunOccurrenceResponse {
  schedule_id: string;
  original_start_ms: number;
  status: ScheduleFireStatus;
  task_id: string | null;
  command: CommandRecord;
  /** The run was High and went as Normal: started by someone who may not
   *  set High, further ahead than today's runs and the next one
   *  (api-server 0.6.56). Absent from an older api-server. */
  priority_lowered?: boolean;
}

/**
 * Dispatch one scheduled occurrence now instead of at its time ("Start
 * now"). Gated server-side on `schedule.manage` AND the caller's own
 * `task.dispatch`; fire mode and an open maintenance window refuse it with
 * 409 and leave the occurrence to run at its own time.
 */
export async function runScheduleOccurrence(
  site: SiteId,
  scheduleId: string,
  originalStartMs: number,
): Promise<RunOccurrenceResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/schedules/${encodeURIComponent(scheduleId)}` +
      `/occurrences/${encodeURIComponent(String(originalStartMs))}/run`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return (await res.json()) as RunOccurrenceResponse;
}

// ----------------------------------------------------------------------------
// Fire mode
// ----------------------------------------------------------------------------

export async function fetchFireMode(site: SiteId): Promise<FireModeState> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/emergency/fire`);
  return (await res.json()) as FireModeState;
}

export async function triggerFireMode(
  site: SiteId,
  body: FireTriggerRequest,
): Promise<FireModeState> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/emergency/fire/trigger`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return (await res.json()) as FireModeState;
}

export async function resolveFireMode(
  site: SiteId,
  body: FireResolveRequest,
): Promise<FireResolveResponse> {
  const res = await apiFetch(
    `/api/v1/sites/${encodeURIComponent(site)}/emergency/fire/resolve`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return (await res.json()) as FireResolveResponse;
}

// ----------------------------------------------------------------------------
// Per-user notification preferences (self-service; not site-scoped)
// ----------------------------------------------------------------------------

export async function fetchNotificationPrefs(): Promise<UserNotificationPrefs> {
  const res = await apiFetch('/api/v1/users/me/notification-prefs');
  return (await res.json()) as UserNotificationPrefs;
}

export async function updateNotificationPrefs(
  body: UpdateUserNotificationPrefs,
): Promise<UserNotificationPrefs> {
  const res = await apiFetch('/api/v1/users/me/notification-prefs', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return (await res.json()) as UserNotificationPrefs;
}

// ----------------------------------------------------------------------------
// Metrics overhaul (2026-10-22): cleaning, lifts & doors, site settings
// ----------------------------------------------------------------------------

export interface MetricsWindowQuery {
  from: string;
  to: string;
  compareFrom?: string | null;
  compareTo?: string | null;
}

function metricsWindowParams(q: MetricsWindowQuery): string {
  const p = new URLSearchParams({ from_date: q.from, to_date: q.to });
  if (q.compareFrom && q.compareTo) {
    p.set('compare_from', q.compareFrom);
    p.set('compare_to', q.compareTo);
  }
  return p.toString();
}

/**
 * True for "this api-server has no such endpoint": a 405, or a 404 that
 * carries no api-server error code (FastAPI's own `{"detail": "Not
 * Found"}` for an unknown route, or a non-JSON body from a proxy). A 404
 * that DOES carry a code (an unknown or retired site, say) is a real
 * refusal and must surface as an error, not as "not available yet".
 */
export function isEndpointMissing(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false;
  if (err.status === 405) return true;
  return err.status === 404 && err.code === 'internal';
}

/** `null` from an api-server that predates the endpoint (see
 *  `isEndpointMissing`). */
async function getOrNullIfMissing(path: string): Promise<unknown | null> {
  try {
    const res = await apiFetch(path);
    return (await res.json()) as unknown;
  } catch (err) {
    if (isEndpointMissing(err)) return null;
    // A proxy's SPA fallback answers an unknown path with index.html.
    if (err instanceof SyntaxError) return null;
    throw err;
  }
}

/**
 * Fire events (alarms and drills) overlapping `[fromMs, toMs)` -- the
 * calendar's Site row. `null` from an api-server older than the endpoint
 * (calendar lanes, 2026-10-30); the caller then falls back to the one
 * event `GET .../emergency/fire` can describe.
 */
export async function fetchFireEvents(
  site: SiteId,
  fromMs: number,
  toMs: number,
): Promise<FireEvent[] | null> {
  const qs = new URLSearchParams({
    from_ms: String(Math.max(0, Math.floor(fromMs))),
    to_ms: String(Math.max(0, Math.floor(toMs))),
  });
  const body = await getOrNullIfMissing(
    `/api/v1/sites/${encodeURIComponent(site)}/emergency/fire/events?${qs.toString()}`,
  );
  if (body === null) return null;
  return Array.isArray(body) ? (body as FireEvent[]) : [];
}

export async function fetchCleaningMetrics(
  site: SiteId,
  q: MetricsWindowQuery,
): Promise<unknown | null> {
  return getOrNullIfMissing(
    `/api/v1/sites/${encodeURIComponent(site)}/metrics/cleaning?${metricsWindowParams(q)}`,
  );
}

export async function fetchLiftMetrics(
  site: SiteId,
  q: MetricsWindowQuery,
): Promise<unknown | null> {
  return getOrNullIfMissing(
    `/api/v1/sites/${encodeURIComponent(site)}/metrics/lifts?${metricsWindowParams({ from: q.from, to: q.to })}`,
  );
}

export async function fetchSiteMetricSettings(site: SiteId): Promise<unknown | null> {
  return getOrNullIfMissing(`/api/v1/sites/${encodeURIComponent(site)}/settings/metrics`);
}

export async function saveSiteMetricSettings(site: SiteId, body: object): Promise<unknown> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/settings/metrics`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return (await res.json()) as unknown;
}

/** `null`: this api-server has no robot groups yet (404). */
export async function fetchRobotGroups(site: SiteId): Promise<unknown | null> {
  return getOrNullIfMissing(`/api/v1/sites/${encodeURIComponent(site)}/settings/robot-groups`);
}

export async function saveRobotGroups(site: SiteId, body: object): Promise<unknown> {
  const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/settings/robot-groups`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return (await res.json()) as unknown;
}

// ----------------------------------------------------------------------------
// Re-export ID helpers for screens
// ----------------------------------------------------------------------------

export type { SiteId, RobotId, TaskId, LiftSession, TaskLogEntry, AuditEntry };

// ----------------------------------------------------------------------------
// Device notes: who did what to a device (`api/device_notes.py`)
// ----------------------------------------------------------------------------

export interface DeviceNotes {
  readonly lift_gate: { readonly by: string | null; readonly at_ms: number } | null;
  readonly robots_out: ReadonlyArray<{
    readonly fleet: string;
    readonly robot: string;
    readonly by: string | null;
    readonly at_ms: number;
  }>;
  readonly auto_charge_off: ReadonlyArray<{
    readonly fleet: string;
    readonly robot: string;
    readonly by: string | null;
    readonly reason: string | null;
    readonly at_ms: number;
  }>;
  readonly door_holds: ReadonlyArray<{
    readonly door: string;
    readonly by: string | null;
    readonly until_ms: number;
    readonly note: string | null;
  }>;
}

/** Null when this api-server has no such read (404): the screens then say
 *  what they can without a name. */
export async function fetchDeviceNotes(site: SiteId): Promise<DeviceNotes | null> {
  try {
    const res = await apiFetch(`/api/v1/sites/${encodeURIComponent(site)}/device-notes`);
    return (await res.json()) as DeviceNotes;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}
